/**
 * AppWrapper — composition root del demo mode.
 *
 * Si `VITE_DEMO_MODE !== 'true'` → render directo de <App /> sin gate ni
 * bootstrap (modo piloto / production normal).
 *
 * Si `VITE_DEMO_MODE === 'true'` → primero LoginGate (usuario del personal, 2026-10-06):
 * el login devuelve el token personal y el device_id del acceso web; después <App />.
 *
 * Patrón malpo (frontend/src/App.tsx): el gate es condicional sobre env var,
 * y el bootstrap del device es additive sin tocar App.tsx mismo.
 */
import { useEffect, useState } from 'react'
import { App } from './App'
import { LoginGate } from './components/LoginGate'
import { leerSesion } from './utils/sesion'
import { Uso } from './components/Uso'
import { SucursalPicker } from './components/SucursalPicker'
import { guardarSucursal, leerSucursal } from './hooks/useSucursal'

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true'

export function AppWrapper() {
  // 2026-10-06: acceso web con usuario del personal (antes, contraseña única en el navegador)
  const [authenticated, setAuthenticated] = useState(!DEMO_MODE || leerSesion() !== null)
  // 2026-09-30 — al entrar la regente elige su sucursal (stock y equivalentes de ahí)
  const [sucursal, setSucursal] = useState(leerSucursal())
  const elegir = (s: NonNullable<typeof sucursal>) => {
    guardarSucursal(s)
    setSucursal(s)
  }
  // 2026-10-10 — tablero de uso (#uso), solo administradores
  const [hash, setHash] = useState(window.location.hash)
  useEffect(() => {
    const f = () => setHash(window.location.hash)
    window.addEventListener('hashchange', f)
    return () => window.removeEventListener('hashchange', f)
  }, [])

  // 26-oct: el enlace hipermaxi.drimian.com/#uso abre el tablero con cualquier sesión (el backend decide quién
  // es administrador; antes había que volver a iniciar sesión para que el navegador lo supiera)
  if (hash === '#uso' && leerSesion() !== null) {
    return <Uso onVolver={() => { window.location.hash = '' }} />
  }

  if (!DEMO_MODE) {
    if (!sucursal) return <SucursalPicker onElegir={elegir} />
    return <App />
  }

  if (!authenticated) {
    return (
      <LoginGate
        onIngreso={() => {
          setSucursal(leerSucursal()) // la cuenta de una sucursal ya trae la suya
          setAuthenticated(true)
        }}
      />
    )
  }

  if (!sucursal) {
    return <SucursalPicker onElegir={elegir} />
  }

  return <App />
}
